import { OrgAdminApiError } from '@/api/orgAdmin';
import { normalizeApiErrorDetails } from '@/api/console';

/**
 * ECR-85: pure helpers for the "Kho lưu trữ" screen — byte/GB conversion, the
 * usage bar's proportions, the PATCH body, and error-code → message-key mapping.
 * Kept JSX-free so they are unit-tested directly.
 *
 * "GB" here is 1024³ bytes. Storage's own seeded quotas are binary
 * (5 GB = 5368709120), so a decimal GB would turn every round quota an operator
 * types back into a ragged one on the next read.
 */
export const BYTES_PER_GB = 1024 ** 3;

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

export function gbToBytes(gb: number): number {
    return Math.round(gb * BYTES_PER_GB);
}

/** Bytes → GB rounded to 2 decimals, for pre-filling the input. */
export function bytesToGb(bytes: number): number {
    return Math.round((bytes / BYTES_PER_GB) * 100) / 100;
}

/** Human-readable size with binary units: `1.5 GB`, `512 MB`, `0 B`. */
export function formatBytes(bytes: number | null | undefined): string {
    const value = Math.max(0, Number(bytes) || 0);
    let unit = 0;
    let scaled = value;
    while (scaled >= 1024 && unit < UNITS.length - 1) {
        scaled /= 1024;
        unit += 1;
    }
    const digits = unit === 0 || scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
    return `${Number(scaled.toFixed(digits))} ${UNITS[unit]}`;
}

export interface UsageShare {
    /** 0–100, share of the quota taken by committed bytes. */
    usedPct: number;
    /** 0–100, share taken by in-flight reservations, never past 100 with `usedPct`. */
    reservedPct: number;
    /** used + reserved as a share of quota, unclamped (can exceed 100). */
    totalPct: number;
}

/**
 * ECR-85: the usage bar draws used + reserved against the effective quota. A zero
 * quota with anything in it is "full", not a division by zero.
 */
export function usageShare(used: number, reserved: number, quota: number): UsageShare {
    const u = Math.max(0, used || 0);
    const r = Math.max(0, reserved || 0);
    if (!quota || quota <= 0) {
        const full = u + r > 0 ? 100 : 0;
        return { usedPct: u > 0 ? 100 : 0, reservedPct: u > 0 ? 0 : full, totalPct: full };
    }
    const usedPct = Math.min(100, (u / quota) * 100);
    const reservedPct = Math.min(100 - usedPct, (r / quota) * 100);
    return { usedPct, reservedPct, totalPct: ((u + r) / quota) * 100 };
}

export type QuotaMode = 'default' | 'custom';

export type QuotaBodyResult =
    | { ok: true; body: { quota_override_bytes: number | null } }
    | { ok: false; errorKey: string };

/**
 * ECR-85: the edit form → the PATCH body. "Dùng mặc định của ứng dụng" sends
 * `null`, which clears the override; a custom value must be a finite number ≥ 0.
 */
export function buildQuotaBody(mode: QuotaMode, gbText: string): QuotaBodyResult {
    if (mode === 'default') {
        return { ok: true, body: { quota_override_bytes: null } };
    }
    const trimmed = gbText.trim().replace(',', '.');
    if (trimmed === '') {
        return { ok: false, errorKey: 'console.storage.edit.errors.required' };
    }
    const gb = Number(trimmed);
    if (!Number.isFinite(gb) || gb < 0) {
        return { ok: false, errorKey: 'console.storage.edit.errors.invalid' };
    }
    return { ok: true, body: { quota_override_bytes: gbToBytes(gb) } };
}

/** portal-api envelope codes this screen tells apart (emg_shared ErrorCodes). */
const CODE_PERMISSION_DENIED = 1009;
const CODE_VALIDATION = 2000;
const CODE_NOT_FOUND = 3000;
const CODE_PROVISIONING_FAILED = 6006;
const CODE_STORAGE_NOT_CONFIGURED = 6007;

export interface SaveErrorMessage {
    key: string;
    /** Storage's own explanation, when it sent one (a 400 it refused). */
    detail?: string;
}

/**
 * ECR-85: a failed save → an i18n key (+ Storage's own reason for a 400). The
 * proxy maps Storage's answers onto portal-api codes; this maps those onto copy.
 */
export function saveErrorMessage(error: unknown): SaveErrorMessage {
    if (!(error instanceof OrgAdminApiError)) {
        return { key: 'console.storage.edit.errors.generic' };
    }
    switch (error.code) {
        case CODE_PERMISSION_DENIED:
            return { key: 'console.storage.edit.errors.forbidden' };
        case CODE_NOT_FOUND:
            return { key: 'console.storage.edit.errors.notFound' };
        case CODE_VALIDATION: {
            const details = normalizeApiErrorDetails(error.details);
            const detail = details.storage?.[0] || details.quota_override_bytes?.[0];
            return { key: 'console.storage.edit.errors.rejected', detail: detail || undefined };
        }
        case CODE_STORAGE_NOT_CONFIGURED:
            return { key: 'console.storage.edit.errors.notConfigured' };
        case CODE_PROVISIONING_FAILED:
            return { key: 'console.storage.edit.errors.unavailable' };
        default:
            return { key: 'console.storage.edit.errors.generic' };
    }
}
